import { useCallback } from 'react';
import { useMutation } from '@apollo/client';
import { useNavigate } from 'react-router';
import { useAuth } from '../../../../providers/AuthProvider';
import { CREATE_SITE_VISIT, UPDATE_SITE_VISIT } from '../../../../graphql/mutations/siteVisit';
import {
  ISiteVisit,
  ISiteVisitPosition,
  ISiteVisitCategory,
  ISiteVisitQuestion,
  ICategoryProgress,
  IQuestionProgress,
  SiteVisitQuestionType,
  QuestionResultMap,
  ISiteVisitProgress,
} from '../../types';

interface UseSiteVisitDataProps {
  siteVisit: ISiteVisit | null;
  setSiteVisit: (siteVisit: ISiteVisit | null) => void;
  position: ISiteVisitPosition;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  onProgress?: (progress: ISiteVisitProgress) => void;
}

export const useSiteVisitData = ({
  siteVisit,
  setSiteVisit,
  position,
  setIsLoading,
  setError,
  onProgress,
}: UseSiteVisitDataProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [createSiteVisitMutation] = useMutation(CREATE_SITE_VISIT);
  const [updateSiteVisitMutation] = useMutation(UPDATE_SITE_VISIT, {
    refetchQueries: ['GetSiteVisit'],
    awaitRefetchQueries: false,
  });

  const saveSiteVisitToServer = useCallback(async (): Promise<void> => {
    if (!siteVisit) return;

    try {
      setIsLoading(true);
      setError(null);

      if (!siteVisit.id) {
        const { data } = await createSiteVisitMutation({
          variables: {
            input: {
              title: siteVisit.title,
              map: siteVisit.map,
              preview: siteVisit.preview,
              categories: siteVisit.categories,
              userId: user?.id,
            },
          },
        });

        if (data?.siteVisitCreate) {
          if (siteVisit) {
            setSiteVisit({ ...siteVisit, id: data.siteVisitCreate.id });
          }
        }
      } else {
        await updateSiteVisitMutation({
          variables: {
            id: siteVisit.id,
            input: {
              title: siteVisit.title,
              map: siteVisit.map,
              preview: siteVisit.preview,
              categories: siteVisit.categories,
              userId: siteVisit.userId,
            },
          },
        });
      }
    } catch (err) {
      console.error('Failed to save site visit:', err);
      setError('Failed to save. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [
    siteVisit,
    createSiteVisitMutation,
    updateSiteVisitMutation,
    user?.id,
    setIsLoading,
    setError,
    setSiteVisit,
  ]);

  const getCurrentCategory = useCallback((): ISiteVisitCategory | null => {
    if (!siteVisit || !siteVisit.categories[position.categoryIndex]) return null;
    return siteVisit.categories[position.categoryIndex];
  }, [siteVisit, position.categoryIndex]);

  const getCurrentQuestion = useCallback((): ISiteVisitQuestion | null => {
    const category = getCurrentCategory();
    if (!category || !category.questions[position.questionIndex]) return null;
    return category.questions[position.questionIndex];
  }, [getCurrentCategory, position.questionIndex]);

  const calculateProgress = useCallback((targetSiteVisit: ISiteVisit | null): ISiteVisitProgress => {
    if (!targetSiteVisit) return { completed: 0, total: 0, percentage: 0 };

    let completed = 0;
    let total = 0;

    for (const category of targetSiteVisit.categories) {
      for (const question of category.questions) {
        total++;
        if (question.result) {
          completed++;
        }
      }
    }

    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { completed, total, percentage };
  }, []);

  const updateQuestionResult = useCallback(
    <T extends SiteVisitQuestionType>(
      result: QuestionResultMap[T] | undefined,
      categoryIndex?: number,
      questionIndex?: number
    ) => {
      if (!siteVisit) return;

      const catIndex = categoryIndex !== undefined ? categoryIndex : position.categoryIndex;
      const qIndex = questionIndex !== undefined ? questionIndex : position.questionIndex;

      if (
        catIndex < 0 ||
        catIndex >= siteVisit.categories.length ||
        qIndex < 0 ||
        qIndex >= siteVisit.categories[catIndex].questions.length
      ) {
        return;
      }

      const updatedSiteVisit = { ...siteVisit };
      updatedSiteVisit.categories = [...siteVisit.categories];
      updatedSiteVisit.categories[catIndex] = {
        ...siteVisit.categories[catIndex],
        questions: [...siteVisit.categories[catIndex].questions],
      };

      const currentQuestion = updatedSiteVisit.categories[catIndex].questions[qIndex];

      switch (currentQuestion.type) {
        case SiteVisitQuestionType.map:
          updatedSiteVisit.categories[catIndex].questions[qIndex] = {
            ...currentQuestion,
            result: result as QuestionResultMap[SiteVisitQuestionType.map],
          };
          break;
        case SiteVisitQuestionType.text:
          updatedSiteVisit.categories[catIndex].questions[qIndex] = {
            ...currentQuestion,
            result: result as QuestionResultMap[SiteVisitQuestionType.text],
          };
          break;
        case SiteVisitQuestionType.number:
          updatedSiteVisit.categories[catIndex].questions[qIndex] = {
            ...currentQuestion,
            result: result as QuestionResultMap[SiteVisitQuestionType.number],
          };
          break;
        case SiteVisitQuestionType.select:
          updatedSiteVisit.categories[catIndex].questions[qIndex] = {
            ...currentQuestion,
            result: result as QuestionResultMap[SiteVisitQuestionType.select],
          };
          break;
        case SiteVisitQuestionType.photos:
          updatedSiteVisit.categories[catIndex].questions[qIndex] = {
            ...currentQuestion,
            result: result as QuestionResultMap[SiteVisitQuestionType.photos],
          };
          break;
        case SiteVisitQuestionType.files:
          updatedSiteVisit.categories[catIndex].questions[qIndex] = {
            ...currentQuestion,
            result: result as QuestionResultMap[SiteVisitQuestionType.files],
          };
          break;
      }

      setSiteVisit(updatedSiteVisit);
      if (onProgress) {
        onProgress(calculateProgress(updatedSiteVisit));
      }
    },
    [siteVisit, position, setSiteVisit, onProgress, calculateProgress]
  );

  const isComplete = useCallback((): boolean => {
    if (!siteVisit) return false;

    for (const category of siteVisit.categories) {
      for (const question of category.questions) {
        if (question.required && !question.result) {
          return false;
        }
      }
    }

    return true;
  }, [siteVisit]);

  const getProgress = useCallback(() => calculateProgress(siteVisit), [calculateProgress, siteVisit]);

  const getCategoriesProgress = useCallback((): ICategoryProgress[] => {
    if (!siteVisit) return [];

    return siteVisit.categories.map(category => {
      let completed = 0;
      const total = category.questions.length;

      for (const question of category.questions) {
        if (question.result) {
          completed++;
        }
      }

      const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

      return {
        category,
        completed,
        total,
        percentage,
      };
    });
  }, [siteVisit]);

  const getCategoryQuestions = useCallback(
    (categoryIndex: number): IQuestionProgress[] => {
      if (!siteVisit || categoryIndex < 0 || categoryIndex >= siteVisit.categories.length) {
        return [];
      }

      const category = siteVisit.categories[categoryIndex];

      return category.questions.map((question, index) => ({
        question,
        isCompleted: !!question.result,
        index,
      }));
    },
    [siteVisit]
  );

  const saveSiteVisitAndRedirect = useCallback(async (redirect = true): Promise<void> => {
    if (!siteVisit) return;
    try {
      await saveSiteVisitToServer();
      if (redirect) navigate(`/site-visit/${siteVisit.id}`);
    } catch (err) {
      console.error('Failed to save site visit:', err);
      setError('Failed to save data. Please try again.');
    }
  }, [siteVisit, saveSiteVisitToServer, navigate, setError]);

  return {
    saveSiteVisitToServer,
    getCurrentCategory,
    getCurrentQuestion,
    updateQuestionResult,
    isComplete,
    getProgress,
    getCategoriesProgress,
    getCategoryQuestions,
    saveSiteVisitAndRedirect,
  };
};
